import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, ShieldAlert, FileWarning, Banknote, UserCheck, ScrollText } from "lucide-react";
import { StatCard, SectionHeader, ProgressRow } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import { useAdminProfiles } from "@/hooks/admin/useAdminMetrics";

const statusTone: Record<string, string> = {
  verified: "bg-emerald-600",
  pending: "bg-amber-600",
  rejected: "bg-red-600",
  unverified: "bg-muted text-foreground",
};

const ComplianceCenter = ({ onNavigate }: { onNavigate?: (tab: string) => void }) => {
  const { data: profiles = [], isLoading, error, refetch } = useAdminProfiles();
  const [search, setSearch] = useState("");

  const paidTiers = useMemo(
    () => profiles.filter((p) => p.user_type === "creator" || p.user_type === "venue"),
    [profiles],
  );

  const stats = useMemo(() => {
    const status = (s: string) => profiles.filter((p) => (p.verification_status ?? "unverified") === s).length;
    return {
      verified: status("verified"),
      pending: status("pending"),
      rejected: status("rejected"),
      unverified: status("unverified"),
      bankingComplete: paidTiers.filter((p) => p.account_number && p.bank_name).length,
      bankingMissing: paidTiers.filter((p) => !p.account_number || !p.bank_name).length,
      subscribed: paidTiers.filter((p) => p.subscription_status === "active").length,
    };
  }, [profiles, paidTiers]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    const rows = profiles.filter((p) => (p.verification_status ?? "unverified") !== "verified");
    if (!t) return rows.slice(0, 100);
    return rows
      .filter((p) => [p.username, p.full_name, p.portal_id, p.city].some((v) => (v ?? "").toLowerCase().includes(t)))
      .slice(0, 100);
  }, [profiles, search]);

  if (isLoading) return <LoadingState label="Loading compliance data…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Compliance & risk"
        description="Venue badge verification, obligation coverage and enforcement actions across the platform."
      />

      {onNavigate && (
        <div className="flex flex-wrap gap-2">
          {[
            { id: "venue-badges", label: "Venue badge verification" },
            { id: "event-approvals", label: "Event approvals" },
            { id: "content-moderation", label: "Content moderation" },
            { id: "policy-enforcement", label: "Policy enforcement" },
            { id: "account-controls", label: "Account controls" },
          ].map((l) => (
            <Button key={l.id} size="sm" variant="outline" onClick={() => onNavigate(l.id)}>
              {l.label}
            </Button>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Verified" value={stats.verified} icon={ShieldCheck} tone="positive" />
        <StatCard label="Awaiting review" value={stats.pending} icon={ShieldAlert} tone="warning" />
        <StatCard label="Rejected / flagged" value={stats.rejected} icon={FileWarning} tone="danger" />
        <StatCard
          label="Banking incomplete"
          value={stats.bankingMissing}
          icon={Banknote}
          tone={stats.bankingMissing > 0 ? "warning" : "positive"}
          hint={`${stats.bankingComplete} payout-ready`}
        />
      </div>

      <Tabs defaultValue="queue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="queue">Review queue</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="policies">Policy controls</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-4">
          <Input
            placeholder="Search by name, @username, portal ID or city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          {filtered.length === 0 ? (
            <EmptyState icon={UserCheck} title="Nothing outstanding" description="Every account has cleared verification." />
          ) : (
            <div className="space-y-2">
              {filtered.map((p) => {
                const status = p.verification_status ?? "unverified";
                return (
                  <Card key={p.id} className="bg-card border-border">
                    <CardContent className="flex flex-wrap items-center gap-4 p-4">
                      <div className="min-w-[220px] flex-1">
                        <p className="font-semibold text-foreground">{p.full_name || p.username || "Unnamed account"}</p>
                        <p className="text-xs text-muted-foreground">
                          @{p.username ?? "unknown"} · {p.user_type ?? "groover"} · {p.city ?? "no city"}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{p.portal_id ?? "—"}</span>
                      <Badge className={`${statusTone[status] ?? "bg-muted"} border-0 capitalize`}>{status}</Badge>
                      {(p.user_type === "creator" || p.user_type === "venue") && !p.account_number && (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-500">
                          Banking missing
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="coverage">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground">Obligation coverage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ProgressRow label="KYC verified" value={stats.verified} total={profiles.length || 1} />
              <ProgressRow label="Paid tiers with banking on file" value={stats.bankingComplete} total={paidTiers.length || 1} />
              <ProgressRow label="Active subscriptions (paid tiers)" value={stats.subscribed} total={paidTiers.length || 1} />
              <ProgressRow label="Never submitted documents" value={stats.unverified} total={profiles.length || 1} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                title: "Role separation",
                body: "Privileged access is stored in a dedicated roles table and evaluated server-side — never from client storage.",
              },
              {
                title: "Row-level security",
                body: "Every platform table enforces row-level security so members only reach their own records.",
              },
              {
                title: "Document retention",
                body: "Verification documents live in a private bucket; only reviewers with an admin role can open them.",
              },
              {
                title: "Payout controls",
                body: "Withdrawals require admin approval before funds are released to a verified bank account.",
              },
            ].map((p) => (
              <Card key={p.title} className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base text-foreground">
                    <ScrollText className="h-4 w-4" /> {p.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceCenter;
